import { useEffect, useState } from "react";
import { UserPlus, Copy, Check, Pencil, Send, Lightbulb, Info } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import { STAFF_ADMIN_ROLE, STAFF_PRIVILEGES, STAFF_ROLES, staffRoleLabel } from "@/constants/roles";
import ListSearchBar from "@/components/ListSearchBar";

const STATUS_LABELS = { active: "Active", inactive: "Inactive", pending_invite: "Invite Sent" };

const errorText = (e, fallback) => {
  const detail = e.response?.data?.detail;
  return Array.isArray(detail) ? detail.map((d) => d.msg || d.message).join(" ") : detail || fallback;
};

const accessSummary = (m) => {
  if (m.role === STAFF_ADMIN_ROLE) return "Everything";
  const labels = STAFF_PRIVILEGES.filter((p) => (m.privileges || []).includes(p.value)).map((p) => p.label);
  return labels.length ? labels.join(", ") : "Dashboard only";
};

// Which role is which, shown above the role choice in both dialogs.
function RoleTip() {
  return (
    <div className="flex gap-2.5 rounded-lg border border-amber-100 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900" data-testid="staff-role-tip">
      <Lightbulb size={14} className="text-amber-500 shrink-0 mt-0.5" />
      <div className="space-y-1">
        <div className="font-semibold">Tip: which role to pick</div>
        {STAFF_ROLES.map((r) => (
          <div key={r.value}><span className="font-semibold">{r.label}:</span> {r.tip}</div>
        ))}
      </div>
    </div>
  );
}

// Role radio + privilege checkboxes, shared by the invite and edit dialogs.
function RoleAndPrivileges({ role, setRole, privileges, setPrivileges, testPrefix }) {
  const toggle = (p) => setPrivileges((ps) => (ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]));
  return (
    <>
      <RoleTip />
      <div>
        <label className="text-[13px] font-medium">Role</label>
        <div className="mt-1.5 space-y-1.5">
          {STAFF_ROLES.map((r) => (
            <label key={r.value} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 cursor-pointer ${role === r.value ? "border-sky-400 bg-sky-50" : "border-slate-200"}`}>
              <input type="radio" name={`${testPrefix}-role`} checked={role === r.value} onChange={() => setRole(r.value)} data-testid={`${testPrefix}-role-${r.value}`} className="mt-0.5" />
              <span>
                <span className="block text-[13px] font-semibold">{r.label}</span>
                <span className="block text-[12px] text-slate-500">{r.description}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div>
        <label className="text-[13px] font-medium">Privileges</label>
        {role === STAFF_ADMIN_ROLE ? (
          <p className="mt-1 text-[12px] text-slate-500">Platform Admins have every privilege.</p>
        ) : (
          <div className="mt-1.5 space-y-1.5">
            {STAFF_PRIVILEGES.map((p) => (
              <label key={p.value} className="flex items-start gap-2.5 text-[13px] cursor-pointer">
                <input type="checkbox" checked={privileges.includes(p.value)} onChange={() => toggle(p.value)} data-testid={`${testPrefix}-privilege-${p.value}`} className="mt-0.5" />
                <span>
                  <span className="block font-medium">{p.label}</span>
                  <span className="block text-[12px] text-slate-500">{p.description}</span>
                </span>
              </label>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default function StaffTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [search, setSearch] = useState("");
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("staff_member");
  const [privileges, setPrivileges] = useState([]);
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editRole, setEditRole] = useState("staff_member");
  const [editPrivileges, setEditPrivileges] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/staff/team").then((r) => setTeam(r.data)).catch((e) => toast.error(errorText(e, "Couldn't load the staff team")));
  useEffect(() => { load(); }, []);

  const openInvite = () => {
    setName(""); setEmail(""); setRole("staff_member"); setPrivileges([]); setInviteLink(null); setCopied(false);
    setShow(true);
  };

  const invite = async () => {
    if (!name.trim() || !email.trim()) return toast.error("Name and email required");
    setSaving(true);
    try {
      const { data } = await api.post("/staff/team", { name, email, role, privileges });
      setInviteLink(`${window.location.origin}/accept-invite?token=${data.invite_token}`);
      toast.success(`Invite emailed to ${data.email}`);
      load();
    } catch (e) {
      toast.error(errorText(e, "Failed to invite"));
    } finally {
      setSaving(false);
    }
  };

  const resend = async (m) => {
    try {
      const { data } = await api.post(`/staff/team/${m.id}/resend-invite`);
      setInviteLink(`${window.location.origin}/accept-invite?token=${data.invite_token}`);
      setCopied(false);
      setShow(true);
      toast.success(`New invite emailed to ${m.email}`);
    } catch (e) {
      toast.error(errorText(e, "Failed to resend"));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      toast.error("Couldn't access the clipboard — select and copy the link manually");
    }
  };

  const openEdit = (m) => { setEditing(m); setEditRole(m.role); setEditPrivileges(m.privileges || []); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/staff/team/${editing.id}`, { role: editRole, privileges: editPrivileges });
      toast.success(`${editing.name} updated`);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errorText(e, "Failed to update"));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (m) => {
    const status = m.status === "active" ? "inactive" : "active";
    try {
      await api.patch(`/staff/team/${m.id}`, { status });
      toast.success(status === "inactive" ? `${m.name} deactivated` : `${m.name} reactivated`);
      load();
    } catch (e) {
      toast.error(errorText(e, "Failed to update"));
    }
  };

  // The staff team is small, so it's filtered right here.
  const words = search.toLowerCase().split(/\s+/).filter(Boolean);
  const shown = team.filter((m) => {
    const hay = `${m.name} ${m.email} ${staffRoleLabel(m.role)} ${accessSummary(m)}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });

  return (
    <div className="p-6 md:p-8" data-testid="staff-team-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Staff Team</h1>
          <p className="text-slate-500 text-sm mt-1">Invite ScapeSource staff and choose what each person can access.</p>
        </div>
        <button onClick={openInvite} data-testid="invite-staff-button" className="w-full sm:w-auto flex items-center justify-center gap-2 h-11 sm:h-auto px-4 sm:py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <UserPlus size={14} /> Invite Staff
        </button>
      </div>

      <ListSearchBar value={search} onChange={setSearch} placeholder="Search staff by name, email, role, access…" testId="staff-team-search" />

      <div className="sm:hidden mt-5 space-y-2">
        {shown.map((m) => (
          <div key={m.id} className="border border-slate-200 rounded-xl p-3.5" data-testid={`staff-card-${m.id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-[14px] truncate">{m.name}</span>
              <span className="text-[12px] text-slate-500 shrink-0">{STATUS_LABELS[m.status] || m.status}</span>
            </div>
            <div className="text-[12.5px] text-slate-500 truncate">{m.email}</div>
            <div className="mt-1.5 text-[12.5px]"><span className="font-medium">{staffRoleLabel(m.role)}</span> · <span className="text-slate-500">{accessSummary(m)}</span></div>
            <div className="mt-2.5 flex gap-2">
              <button onClick={() => openEdit(m)} className="flex-1 h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Edit access</button>
              {m.status === "pending_invite" ? (
                <button onClick={() => resend(m)} className="flex-1 h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold">Resend invite</button>
              ) : m.id !== user?.id && (
                <button onClick={() => toggleStatus(m)} className="flex-1 h-10 rounded-lg border border-slate-200 text-[12.5px] font-semibold">{m.status === "active" ? "Deactivate" : "Reactivate"}</button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="staff-team-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Email</th><th className="text-left py-2.5">Role</th>
              <th className="text-left py-2.5">Access</th><th className="text-left py-2.5">Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {shown.map((m) => (
              <tr key={m.id} className="border-b border-slate-50" data-testid={`staff-row-${m.id}`}>
                <td className="py-3 font-medium">{m.name}{m.id === user?.id && <span className="text-slate-400 font-normal"> (you)</span>}</td>
                <td className="py-3 text-slate-500">{m.email}</td>
                <td className="py-3">
                  <span className="inline-flex items-center gap-1" title={STAFF_ROLES.find((r) => r.value === m.role)?.tip}>
                    {staffRoleLabel(m.role)} <Info size={12} className="text-slate-400" />
                  </span>
                </td>
                <td className="py-3 text-slate-500 text-[12.5px]">{accessSummary(m)}</td>
                <td className="py-3">{STATUS_LABELS[m.status] || m.status}</td>
                <td className="py-3 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(m)} data-testid={`edit-staff-${m.id}`} title="Edit role and privileges" className="text-slate-400 hover:text-sky-600 mr-3"><Pencil size={14} /></button>
                  {m.status === "pending_invite" ? (
                    <button onClick={() => resend(m)} data-testid={`resend-staff-invite-${m.id}`} className="text-[12px] font-semibold text-sky-600 inline-flex items-center gap-1"><Send size={12} /> Resend</button>
                  ) : m.id !== user?.id && (
                    <button onClick={() => toggleStatus(m)} data-testid={`toggle-staff-status-${m.id}`} className="text-[12px] font-semibold text-sky-600">
                      {m.status === "active" ? "Deactivate" : "Reactivate"}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={(open) => { if (!open) setShow(false); }}>
        <DialogContent data-testid="invite-staff-modal">
          {!inviteLink ? (
            <>
              <DialogHeader><DialogTitle>Invite Staff</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="invite-staff-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email" data-testid="invite-staff-email-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                <RoleAndPrivileges role={role} setRole={setRole} privileges={privileges} setPrivileges={setPrivileges} testPrefix="invite" />
                <button disabled={saving} onClick={invite} data-testid="confirm-invite-staff-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">{saving ? "Sending…" : "Send Invite"}</button>
              </div>
            </>
          ) : (
            <>
              <DialogHeader><DialogTitle>Invite Sent</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <p className="text-[13px] text-slate-500">We emailed the invite. You can also share this link directly — it expires in 7 days and works once.</p>
                <div className="flex items-center gap-2">
                  <input readOnly value={inviteLink} data-testid="staff-invite-link-input" className="flex-1 min-w-0 h-10 px-3 rounded-lg border border-slate-200 text-[12.5px] font-mono text-slate-600" />
                  <button onClick={copyLink} className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 flex items-center gap-1.5 text-[12.5px] font-semibold shrink-0">
                    {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
                  </button>
                </div>
                <button onClick={() => setShow(false)} className="w-full h-10 rounded-lg bg-slate-900 text-white text-[13px] font-semibold">Done</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent data-testid="edit-staff-modal">
          <DialogHeader><DialogTitle>Edit {editing?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <RoleAndPrivileges role={editRole} setRole={setEditRole} privileges={editPrivileges} setPrivileges={setEditPrivileges} testPrefix="edit" />
            <button disabled={saving} onClick={saveEdit} data-testid="save-staff-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold disabled:opacity-60">{saving ? "Saving…" : "Save"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
